import * as React from "react";
import { useState, useEffect } from "react";
import AntDTable from "@/common/components/ui/antd-table";
import { Card, CardContent } from "@/common/components/ui/card";
import { Badge } from "@/common/components/ui/badge";
import { Button } from "@/common/components/ui/button";
import { Input } from "@/common/components/ui/input";
import LogsService from "@/services/FinEaseHomeAndAdmin/APIs/LogsService";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import {
    AlertCircle,
    LogIn,
    Search,
    RefreshCw,
    Download,
    Filter,
    Users,
    Eye,
    Globe,
    Laptop,
    Shield,
    Key
} from "lucide-react";
import CommonModal from "@/common/components/ui/modal";
import * as XLSX from "xlsx";
import { notify } from "@/common/components/common/Notification";

const LoginActivityLogs = () => {
    const [loading, setLoading] = useState(false);
    const { toast } = useToast();

    // Data States
    const [loginLogs, setLoginLogs] = useState<any[]>([]);
    const [selectedLog, setSelectedLog] = useState<any | null>(null);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [selectedRows, setSelectedRows] = useState<any[]>([]);

    // Pagination States
    const [loginPagination, setLoginPagination] = useState({ current: 1, pageSize: 10, total: 0 });

    const fetchLogs = async (page = 1, pageSize = 10) => {
        setLoading(true);
        try {
            const res: any = await LogsService.getLoginLogsAPI(page, pageSize);
            const data = res.results ? res : res.data;
            if (data?.results) {
                setLoginLogs(data.results);
                setLoginPagination({ current: data.page, pageSize: data.page_size, total: data.count });
            }
        } catch (error: any) {
            toast({
                variant: "destructive",
                title: "Error fetching logs",
                description: error.message ?? "Something went wrong",
            });
        } finally {
            setLoading(false);
        }
    };

    // Initial Fetch
    useEffect(() => {
        fetchLogs(loginPagination.current, loginPagination.pageSize);
    }, []);

    const handleRefresh = () => {
        fetchLogs(loginPagination.current, loginPagination.pageSize);
        toast({ title: "Refreshed", description: "Login logs updated." });
    };

    const handlePageChange = (page: number, pageSize: number) => {
        fetchLogs(page, pageSize);
    };

    const formatDate = (dateUnparsed: string) => {
        try {
            return format(new Date(dateUnparsed), "MMM dd, yyyy HH:mm:ss");
        } catch (_) {
            return dateUnparsed;
        }
    };

    const onSelectChange = (keys: React.Key[], rows: any[]) => {
        setSelectedRowKeys(keys);
        setSelectedRows(rows);
    };

    const handleExport = () => {
        if (selectedRows.length === 0) {
            notify.error("No Data", "Please select items to export.");
            return;
        }

        const rows = selectedRows.map(item => ({
            Timestamp: item.created_on ? format(new Date(item.created_on), "MMM dd, yyyy HH:mm:ss") : "-",
            User: item.user_email ?? item.identifier_used ?? "Unknown",
            Tenant: item.tenant_name ?? "-",
            IP: item.ip_address ?? "-",
            Success: item.success ? "Yes" : "No",
            Method: item.login_method ?? "-",
            "Failure Reason": item.failure_reason ?? "-",
        }));

        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(rows);
        XLSX.utils.book_append_sheet(wb, ws, "LoginLogs");

        XLSX.writeFile(
            wb,
            `login_activity_logs_${new Date().toISOString().split("T")[0]}.xlsx`
        );
        notify.success("Exported", `${rows.length} record(s) exported.`);
    };

    // --- Columns Definitions ---

    const loginColumns = [
        {
            title: "Timestamp",
            dataIndex: "created_on",
            key: "created_on",
            width: 250,
            render: (text: string) => (
                <div className="flex flex-col">
                    <span className="font-medium text-gray-700 text-sm">{formatDate(text)}</span>
                    <span className="text-xs text-muted-foreground">{format(new Date(text), "PP")}</span>
                </div>
            ),
        },
        {
            title: "User Identity",
            key: "user",
            width: 250,
            render: (_: any, record: any) => (
                <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900 text-sm">
                            {record.user_email ?? record.identifier_used ?? "Unknown User"}
                        </span>
                        {record.tenant_name && (
                            <Badge variant="outline" className="text-[10px] py-0 h-4 border-gray-300 text-gray-500">
                                {record.tenant_name}
                            </Badge>
                        )}
                    </div>

                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1 bg-gray-100 px-1.5 py-0.5 rounded">
                            <Globe className="w-3 h-3" /> {record.ip_address}
                        </span>
                    </div>
                </div>
            )
        },
        {
            title: "Result",
            key: "success",
            width: 150,
            render: (_: any, record: any) => (
                <Badge
                    className={`
                        ${record.success
                            ? "bg-green-100 text-green-700 border-green-200"
                            : "bg-red-100 text-red-700 border-red-200"
                        } border shadow-sm px-2 py-0.5
                    `}
                >
                    {record.success ? "Success" : "Failed"}
                </Badge>
            ),
        },
        {
            title: "Context",
            key: "context",
            width: 250,
            render: (_: any, record: any) => (
                <div className="flex flex-col gap-1 text-xs">
                    <div className="flex items-center gap-1.5 text-gray-600">
                        <Key className="w-3.5 h-3.5 text-gray-400" />
                        <span>via <span className="font-medium text-gray-900">{record.login_method ?? "Unknown"}</span></span>
                    </div>
                    {!record.success && record.failure_reason && (
                        <div className="flex items-start gap-1.5 text-red-600 max-w-[200px]">
                            <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                            <span className="truncate" title={record.failure_reason}>{record.failure_reason}</span>
                        </div>
                    )}
                </div>
            )
        },
        {
            title: "Details",
            key: "action",
            width: 80,
            align: "right" as const,
            render: (_: any, record: any) => (
                <Button
                    variant="ghost"
                    size="sm"
                    className="hover:bg-purple-50 hover:text-purple-600"
                    onClick={(e) => {
                        e.stopPropagation();
                        setSelectedLog(record);
                    }}
                >
                    <Eye className="w-4 h-4 mr-1.5" />
                    View
                </Button>
            )
        }
    ];

    return (
        <>
            <div className="min-h-screen bg-gray-50/50 p-6 space-y-8 font-sans">
                {/* Header Section */}
                <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-secondary via-secondary/95 to-primary/80 p-6 md:p-8">
                    <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent" />
                    <div className="absolute right-0 top-0 opacity-10">
                        <Users className="h-48 w-48 -translate-y-8 translate-x-12 transform text-white" />
                    </div>
                    <div className="relative flex flex-col justify-between gap-4 md:flex-row md:items-center">
                        <div className="flex items-center gap-4">
                            <div className="rounded-xl bg-white/10 p-3 backdrop-blur-sm">
                                <LogIn className="h-7 w-7 text-white" />
                            </div>
                            <div>
                                <h1 className="font-display text-2xl font-bold text-white">
                                    Login Activity Logs
                                </h1>
                                <p className="text-white/70">
                                    Monitor authentication attempts, security access, and user sessions.
                                </p>
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-3">
                            <Button
                                variant="outline"
                                className="bg-white/10 border-white/20 text-white hover:bg-white/20 hover:text-white transition-all backdrop-blur-md"
                                onClick={handleRefresh}
                            >
                                <RefreshCw className="mr-2 h-4 w-4" />
                                Sync Data
                            </Button>
                            <Button
                                variant="outline"
                                className="gap-2 border-white/20 bg-white/10 text-white hover:bg-white/20"
                                onClick={handleExport}
                                disabled={selectedRows.length === 0}
                            >
                                <Download className="h-4 w-4" />
                                Export
                                {selectedRows.length > 0 && (
                                    <span className="ml-1 rounded-full bg-white/25 px-1.5 py-0.5 text-[11px] font-semibold leading-none">
                                        {selectedRows.length}
                                    </span>
                                )}
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Stats Overview */}
                <div className="grid gap-6 md:grid-cols-1">
                    <Card className="overflow-hidden border-none shadow-sm hover:shadow-md transition-shadow">
                        <CardContent className="p-6">
                            <div className="flex items-center justify-between">
                                <div className="space-y-1">
                                    <p className="text-sm font-medium text-muted-foreground">Total Login Attempts</p>
                                    <div className="flex items-baseline gap-2">
                                        <h2 className="text-3xl font-bold text-gray-900">{loginPagination.total}</h2>
                                        <span className="text-xs text-purple-600 font-medium bg-purple-50 px-2 py-0.5 rounded-full">All Time</span>
                                    </div>
                                </div>
                                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-purple-50">
                                    <Users className="h-6 w-6 text-purple-600" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Main Content Area */}
                {/* Toolbar & Filters */}
                <div className="border-b p-5 bg-white">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                        <div className="relative max-w-sm w-full">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                                placeholder="Search by email..."
                                className="pl-9 bg-gray-50 border-gray-200 focus:bg-white transition-colors"
                            />
                        </div>

                        <div className="flex items-center gap-3">
                            <Button variant="outline" size="icon" className="h-10 w-10 text-muted-foreground">
                                <Filter className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Table Content */}
                <div className="p-4 bg-gray-50/40 border-b flex justify-between items-center text-xs text-muted-foreground">
                    <span>Showing {loginLogs.length} recent records</span>
                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-purple-500" /> Live</span>
                </div>
                <AntDTable
                    columns={loginColumns}
                    data={loginLogs}
                    loading={loading}
                    showSettings
                    pagination={{
                        current: loginPagination.current,
                        pageSize: loginPagination.pageSize,
                        total: loginPagination.total,
                        onChange: handlePageChange,
                        showSizeChanger: true
                    }}
                    rowKey="id"
                    rowSelection={{
                        selectedRowKeys,
                        onChange: onSelectChange,
                    }}
                    className="border-0"
                    onRow={(record) => ({
                        onClick: () => setSelectedLog(record),
                        className: "cursor-pointer hover:bg-gray-50 transition-colors"
                    })}
                />
            </div>

            <CommonModal
                isOpen={!!selectedLog}
                onClose={() => setSelectedLog(null)}
                width="max-w-3xl w-full"
                height="h-auto"
                padding="p-0"
            >
                <div className="flex flex-col">
                    <div className={`border-b rounded-t-full p-6 pb-4`}>
                        <div className="flex items-center gap-4 mb-2">
                            <div className={`p-2 rounded-full ${selectedLog?.success ? "bg-green-100 text-green-600" : "bg-red-100 text-red-600"}`}>
                                {selectedLog?.success ? <Shield className="w-6 h-6" /> : <AlertCircle className="w-6 h-6" />}
                            </div>
                            <div>
                                <h2 className="text-xl font-semibold text-gray-900">
                                    Login {selectedLog?.success ? "Successful" : "Failed"}
                                </h2>
                                <p className="text-sm text-muted-foreground">
                                    {selectedLog && formatDate(selectedLog.created_on)}
                                </p>
                            </div>

                        </div>
                    </div>

                    <div className="p-6">
                        <div className="space-y-6">

                            {/* Failure Reason Banner */}
                            {!selectedLog?.success && selectedLog?.failure_reason && (
                                <div className="bg-red-50 border border-red-100 rounded-lg p-4 flex gap-3 text-red-800">
                                    <AlertCircle className="w-5 h-5 shrink-0" />
                                    <div className="space-y-1">
                                        <p className="font-semibold text-sm">Authentication Failed</p>
                                        <p className="text-sm opacity-90">{selectedLog.failure_reason}</p>
                                    </div>
                                </div>
                            )}

                            {/* Detailed Info Grid */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-sm">
                                <div className="space-y-4">
                                    <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                                        <Globe className="w-4 h-4 text-purple-500" /> User Context
                                    </h4>
                                    <div className="grid grid-cols-[100px_1fr] gap-3">
                                        <span className="text-muted-foreground">Identity:</span>
                                        <div className="flex flex-col">
                                            <span className="font-medium text-gray-900">
                                                {selectedLog?.user_email ?? "N/A"}
                                            </span>
                                            {selectedLog?.identifier_used !== selectedLog?.user_email && (
                                                <span className="text-xs text-muted-foreground">
                                                    Used: {selectedLog?.identifier_used}
                                                </span>
                                            )}
                                        </div>

                                        <span className="text-muted-foreground">Tenant:</span>
                                        <span>{selectedLog?.tenant_name ?? "N/A"}</span>

                                        <span className="text-muted-foreground">IP Address:</span>
                                        <span className="font-mono text-xs bg-gray-100 px-2 py-0.5 rounded w-fit text-gray-700">
                                            {selectedLog?.ip_address}
                                        </span>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                                        <Laptop className="w-4 h-4 text-blue-500" /> Device & Auth
                                    </h4>
                                    <div className="grid grid-cols-[100px_1fr] gap-3">
                                        <span className="text-muted-foreground">Login ID:</span>
                                        <span className="font-mono text-xs text-muted-foreground">
                                            {selectedLog?.id?.split('-')[0]}...
                                        </span>

                                        <span className="text-muted-foreground">Method:</span>
                                        <Badge variant="outline" className="w-fit capitalize">
                                            {selectedLog?.login_method}
                                        </Badge>

                                        <span className="text-muted-foreground">User Agent:</span>
                                        <span className="text-xs text-muted-foreground break-all leading-tight">
                                            {selectedLog?.user_agent}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </CommonModal>
        </>
    );
};

export default LoginActivityLogs;
